import { useMemo } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { getLocalizedUrl, shouldBypassUrlLocalization } from "../utils/url-slug-mapping";

/**
 * Hook that translates a German canonical URL to the current language
 *
 * @param canonicalUrl German canonical URL (e.g., "/kunden" or "/produkte/vertriebsportal-starter")
 * @returns Localized URL with language prefix for non-German languages
 *
 * @example
 * const { language } = useLanguage(); // "en"
 * const url = useLocalizedUrl("/kunden"); // "/en/customers"
 *
 * @example
 * const { language } = useLanguage(); // "de"
 * const url = useLocalizedUrl("/kunden"); // "/kunden" (no prefix for German)
 */
export const useLocalizedUrl = (canonicalUrl: string): string => {
    const { language } = useLanguage();

    return useMemo(() => {
        // Don't localize complete URLs with protocol
        if (canonicalUrl.startsWith("http://") || canonicalUrl.startsWith("https://")) {
            return canonicalUrl;
        }

        // Pure in-page anchors stay as-is — they target the current page, so
        // prefixing them with /en would jump to the English homepage instead.
        if (canonicalUrl.startsWith("#")) {
            return canonicalUrl;
        }

        if (shouldBypassUrlLocalization(canonicalUrl)) {
            return canonicalUrl;
        }

        if (language === "de") {
            return canonicalUrl;
        }

        return getLocalizedUrl(canonicalUrl, language);
    }, [canonicalUrl, language]);
};

/**
 * Hook that translates multiple German canonical URLs to the current language
 *
 * Useful for navigation menus and other components with multiple links.
 *
 * @param urls Record of URLs with descriptive keys
 * @returns Record with same keys but localized URLs
 *
 * @example
 * const urls = useLocalizedUrls({
 *   customers: "/kunden",
 *   products: "/produkte",
 *   about: "/ueber-uns"
 * });
 * // With language "en":
 * // { customers: "/en/customers", products: "/en/products", about: "/en/about-us" }
 */
export const useLocalizedUrls = <T extends Record<string, string>>(urls: T): T => {
    const { language } = useLanguage();

    return useMemo(() => {
        if (language === "de") {
            return urls;
        }

        const localizedUrls = {} as T;
        for (const [key, germanUrl] of Object.entries(urls)) {
            // Don't localize complete URLs with protocol
            if (germanUrl.startsWith("http://") || germanUrl.startsWith("https://")) {
                localizedUrls[key as keyof T] = germanUrl as T[keyof T];
            } else if (germanUrl.startsWith("#")) {
                // Pure in-page anchor — keep as-is (see useLocalizedUrl above)
                localizedUrls[key as keyof T] = germanUrl as T[keyof T];
            } else if (shouldBypassUrlLocalization(germanUrl)) {
                localizedUrls[key as keyof T] = germanUrl as T[keyof T];
            } else {
                localizedUrls[key as keyof T] = getLocalizedUrl(germanUrl, language) as T[keyof T];
            }
        }

        return localizedUrls;
    }, [urls, language]);
};
